import React from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { CourseStatus } from '@edx/graphql/generated';
import { Link } from '@edx/components/link';
import { IconAngleRight, IconCheck, IconPlaySolid } from '@edx/components/icons';
import { GridItemProps } from '@edx/components/media-grid';
import { Badge } from '@edx/components/badge';
import { formatDurationInSeconds } from '@edx/helpers/datetime';
import { Body, Caption, Title } from '@edx/components/typography';

type GridProps = {
  gridColumns?: 2 | 3 | 4;
  textVariant?: 'light' | 'dark';
  titleVariant?: 'title' | 'body';
  variant?: 'video' | 'song';
};

export const GridItem: React.FC<GridItemProps & GridProps> = ({
  title,
  resourcePath,
  subtitle,
  badge,
  status,
  imageUrl,
  description,
  gridColumns,
  viewerCompletionPercentage,
  viewerHasCompleted,
  duration,
  textVariant = 'light',
  titleVariant = 'title',
  variant = 'video',
}) => (
  <li key={resourcePath} className="group hover:bg-gray-600 hover:bg-opacity-10 transition duration-150">
    <Link href={resourcePath}>
      <div className="relative group">
        <Image src={imageUrl} alt={title} width={528} height={variant === 'song' ? 528 : 297} layout="responsive" />

        <div className="absolute items-center justify-center inset-0 flex bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0 transition duration-150">
          <div className="flex">
            <div className="p-2 bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 ease transform translate-y-3 group-hover:translate-y-0">
              <IconPlaySolid className={clsx('w-6', viewerHasCompleted ? 'text-green' : 'text-primary-500')} />
            </div>
          </div>
          {viewerCompletionPercentage ? (
            <div
              className="absolute bottom-0 flex justify-end left-0 border-b-2 border-red-600"
              style={{ width: `${viewerCompletionPercentage}%` }}
            >
              <div className="font-body font-medium text-sm text-transparent group-hover:text-white transition-all duration-150 ease transform translate-y-1 group-hover:translate-y-0 tracking-widest px-2 py-1">
                {viewerCompletionPercentage}%
              </div>
            </div>
          ) : null}
        </div>

        {duration && (
          <span className="absolute z-10 right-0 bottom-0 px-2 py-1 bg-gray-800 text-gray-300 font-medium text-sm tracking-widest bg-opacity-50 group-hover:bg-opacity-0 transition-all duration-200 ease transform group-hover:translate-y-1 group-hover:text-transparent">
            {formatDurationInSeconds(duration)}
          </span>
        )}

        {viewerHasCompleted && (
          <span className="absolute top-0 right-0 bg-green w-7 h-7 flex items-center justify-center">
            <IconCheck className="w-5 h-5 text-white font-bold" />
          </span>
        )}
      </div>

      <div className="flex flex-col font-body p-2 space-y-1">
        <header
          className={clsx('leading-6', {
            'text-gray-300': textVariant === 'light',
            'text-gray-700': textVariant === 'dark',
          })}
        >
          {titleVariant === 'body' ? (
            <Body
              tag="h2"
              size={gridColumns === 4 ? 'large' : 'xlarge'}
              variant="semibold"
              className="inline leading-tight"
            >
              {title}
            </Body>
          ) : (
            <Title tag="h2" size={gridColumns === 4 ? 'small' : 'medium'} className="inline leading-tight">
              {title}
            </Title>
          )}
          {badge && <Badge className="ml-2 flex-shrink align-top">{badge}</Badge>}
        </header>

        <div className="flex space-x-2">
          <Caption
            tag="span"
            variant="semibold"
            className={clsx('leading-relaxed', {
              'text-gray-500': textVariant === 'light',
              'text-gray-700': textVariant === 'dark',
            })}
          >
            {subtitle}
          </Caption>

          {status === CourseStatus.Ongoing && (
            <div className="relative flex items-center space-x-1">
              <div className="relative flex items-center justify-center w-4 h-4">
                <span className="animate-ping absolute h-2 w-2 rounded-full bg-primary-400 opacity-75" />
                <span className="rounded-full h-1 w-1 bg-primary-500 leading-relaxed" />
              </div>
              <span className="text-xs uppercase tracking-widest font-medium leading-relaxed text-primary-500">
                Grabando
              </span>
            </div>
          )}
        </div>

        <Body className="text-gray-500">{description}</Body>

        {variant === 'video' && (
          <footer
            className={clsx('block md:hidden text-sm font-medium flex', {
              'text-gray-300': textVariant === 'light',
              'text-gray-700': textVariant === 'dark',
            })}
          >
            Acceder <IconAngleRight className="w-3" />
          </footer>
        )}
      </div>
    </Link>
  </li>
);
